import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Trash2, Upload, Inbox, User as UserIcon, Save, ExternalLink, Heart, ShieldCheck } from "lucide-react";
import { api, resolveImg, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { EditorCard } from "@/components/EditorCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const EMPTY = {
    name: "", location: "", region: "", photo_url: "", cover_url: "", bio: "",
    genres: [], publishers: [], services: [], starting_price: 0, contact_email: "",
    books_edited: [], own_novels: [],
};

function ImageUpload({ label, value, onChange, testid }) {
    const ref = useRef();
    const [busy, setBusy] = useState(false);
    const upload = async (file) => {
        if (!file) return;
        setBusy(true);
        const fd = new FormData();
        fd.append("file", file);
        try {
            const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
            onChange(data.url);
            toast.success("Immagine caricata");
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
        finally { setBusy(false); }
    };
    return (
        <div>
            <Label>{label}</Label>
            <div className="flex items-center gap-3 mt-1.5">
                {value ? <img src={resolveImg(value)} alt="" className="w-16 h-16 rounded object-cover border border-border" /> :
                    <div className="w-16 h-16 rounded bg-secondary flex items-center justify-center text-muted-foreground"><UserIcon size={20} /></div>}
                <input type="file" accept="image/*" ref={ref} className="hidden" onChange={(e) => upload(e.target.files[0])} data-testid={testid} />
                <Button type="button" variant="outline" size="sm" onClick={() => ref.current.click()} disabled={busy} className="gap-2">
                    <Upload size={14} /> {busy ? "Carico…" : "Carica"}
                </Button>
            </div>
        </div>
    );
}

const genKey = () => (typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : `k_${Math.random().toString(36).slice(2)}`);
const withKeys = (arr) => (arr || []).map((it) => ({ ...it, _key: it._key || genKey() }));

export default function Dashboard() {
    const { user, checkAuth } = useAuth();
    const navigate = useNavigate();
    const [profile, setProfile] = useState(null);
    const [form, setForm] = useState(EMPTY);
    const [quotes, setQuotes] = useState([]);
    const [favorites, setFavorites] = useState([]);
    const [saving, setSaving] = useState(false);
    const [requestingVerif, setRequestingVerif] = useState(false);
    const isEditor = user && (user.role === "editor" || user.role === "admin");

    useEffect(() => {
        if (user === false) { navigate("/auth?mode=login"); return; }
        if (!user) return;
        (async () => {
            if (isEditor) {
                const { data } = await api.get("/editors/mine");
                if (data) { setProfile(data); setForm({ ...EMPTY, ...data, books_edited: withKeys(data.books_edited), own_novels: withKeys(data.own_novels), services: withKeys(data.services) }); }
                else setForm({ ...EMPTY, name: user.name, contact_email: user.email });
                const q = await api.get("/quotes/received");
                setQuotes(q.data);
            } else {
                const { data } = await api.get("/favorites");
                setFavorites(data);
            }
        })();
        // eslint-disable-next-line
    }, [user]);

    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
    const listField = (k, v) => set(k, v.split(",").map((s) => s.trim()).filter(Boolean));

    const addItem = (k, item) => set(k, [...(form[k] || []), { ...item, _key: genKey() }]);
    const updItem = (k, i, patch) => set(k, form[k].map((it, idx) => (idx === i ? { ...it, ...patch } : it)));
    const delItem = (k, i) => set(k, form[k].filter((_, idx) => idx !== i));

    const save = async () => {
        setSaving(true);
        try {
            const payload = { ...form, starting_price: Number(form.starting_price) || 0 };
            if (profile) await api.put(`/editors/${profile.editor_id}`, payload);
            else { const { data } = await api.post("/editors", payload); setProfile(data); await checkAuth(); }
            toast.success("Profilo salvato");
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
        finally { setSaving(false); }
    };

    const setStatus = async (rid, status) => {
        await api.put(`/quotes/${rid}/status`, { status });
        setQuotes((qs) => qs.map((q) => (q.request_id === rid ? { ...q, status } : q)));
    };

    const requestVerification = async () => {
        setRequestingVerif(true);
        try {
            const { data } = await api.post("/editors/mine/request-verification");
            setProfile((p) => ({ ...p, verification_status: data.verification_status }));
            toast.success("Richiesta di verifica inviata all'amministrazione");
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
        finally { setRequestingVerif(false); }
    };

    if (!user) return <div className="py-24 text-center text-muted-foreground">Caricamento…</div>;

    if (!isEditor) {
        return (
            <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
                <h1 className="text-4xl font-serif font-medium">Ciao, {user.name}</h1>
                <p className="text-muted-foreground mt-2 mb-8">I tuoi editor preferiti, sempre a portata di mano.</p>
                {favorites.length === 0 ? (
                    <div className="text-center py-16 bg-paper border border-border rounded-lg" data-testid="empty-favorites">
                        <Heart className="mx-auto text-muted-foreground mb-3" size={28} />
                        <p className="text-muted-foreground">Non hai ancora salvato nessun editor.</p>
                        <Link to="/editors"><Button className="mt-5 bg-primary" data-testid="writer-explore-btn">Esplora gli editor</Button></Link>
                    </div>
                ) : (
                    <>
                        <h2 className="text-2xl font-serif font-medium mb-5 flex items-center gap-2"><Heart size={20} className="text-primary" /> Editor preferiti ({favorites.length})</h2>
                        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                            {favorites.map((e, i) => <EditorCard key={e.editor_id} editor={e} index={i} />)}
                        </div>
                    </>
                )}
            </div>
        );
    }

    return (
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
            <div className="flex items-center justify-between flex-wrap gap-3 mb-8">
                <div>
                    <h1 className="text-4xl font-serif font-medium">Area editor</h1>
                    <p className="text-muted-foreground mt-1">Gestisci il tuo profilo pubblico e le richieste ricevute.</p>
                </div>
                {profile && <Link to={`/editors/${profile.editor_id}`}><Button variant="outline" className="gap-2" data-testid="view-public-profile"><ExternalLink size={15} /> Profilo pubblico</Button></Link>}
            </div>

            {profile && (
                <div className="mb-6 rounded-lg border border-border bg-paper p-4 flex items-center justify-between gap-4 flex-wrap" data-testid="verification-box">
                    {profile.verified ? (
                        <p className="flex items-center gap-2 text-sage font-medium"><ShieldCheck size={18} /> Profilo verificato dall'amministrazione</p>
                    ) : profile.verification_status === "pending" ? (
                        <p className="flex items-center gap-2 text-muted-foreground" data-testid="verif-pending"><ShieldCheck size={18} /> Richiesta di verifica inviata, in attesa di approvazione</p>
                    ) : (
                        <>
                            <p className="text-muted-foreground text-sm">Ottieni il badge <span className="text-foreground font-medium">Verificato</span> per aumentare la fiducia degli scrittori.</p>
                            <Button size="sm" onClick={requestVerification} disabled={requestingVerif} className="gap-2 bg-primary" data-testid="request-verification-btn"><ShieldCheck size={15} /> Richiedi verifica</Button>
                        </>
                    )}
                </div>
            )}

            <Tabs defaultValue="profile">
                <TabsList className="mb-6">
                    <TabsTrigger value="profile" data-testid="tab-profile">Profilo</TabsTrigger>
                    <TabsTrigger value="books" data-testid="tab-books">Libri & Romanzi</TabsTrigger>
                    <TabsTrigger value="services" data-testid="tab-services">Tariffe</TabsTrigger>
                    <TabsTrigger value="quotes" data-testid="tab-quotes" className="gap-1"><Inbox size={14} /> Richieste ({quotes.length})</TabsTrigger>
                </TabsList>

                <TabsContent value="profile" className="space-y-5">
                    <div className="grid sm:grid-cols-2 gap-5">
                        <ImageUpload label="Foto profilo" value={form.photo_url} onChange={(u) => set("photo_url", u)} testid="upload-photo" />
                        <ImageUpload label="Immagine di copertina" value={form.cover_url} onChange={(u) => set("cover_url", u)} testid="upload-cover" />
                    </div>
                    <div className="grid sm:grid-cols-2 gap-5">
                        <div><Label>Nome</Label><Input value={form.name} onChange={(e) => set("name", e.target.value)} className="mt-1.5" data-testid="profile-name" /></div>
                        <div><Label>Email contatto</Label><Input value={form.contact_email} onChange={(e) => set("contact_email", e.target.value)} className="mt-1.5" data-testid="profile-email" /></div>
                        <div><Label>Città</Label><Input value={form.location} onChange={(e) => set("location", e.target.value)} className="mt-1.5" data-testid="profile-city" /></div>
                        <div><Label>Regione</Label><Input value={form.region} onChange={(e) => set("region", e.target.value)} className="mt-1.5" data-testid="profile-region" /></div>
                    </div>
                    <div><Label>Bio professionale</Label><Textarea value={form.bio} onChange={(e) => set("bio", e.target.value)} rows={5} className="mt-1.5" data-testid="profile-bio" /></div>
                    <div className="grid sm:grid-cols-3 gap-5">
                        <div><Label>Generi (separati da virgola)</Label><Input value={(form.genres || []).join(", ")} onChange={(e) => listField("genres", e.target.value)} className="mt-1.5" data-testid="profile-genres" /></div>
                        <div><Label>Case editrici (virgola)</Label><Input value={(form.publishers || []).join(", ")} onChange={(e) => listField("publishers", e.target.value)} className="mt-1.5" data-testid="profile-publishers" /></div>
                        <div><Label>Prezzo base (€/cartella)</Label><Input type="number" value={form.starting_price} onChange={(e) => set("starting_price", e.target.value)} className="mt-1.5" data-testid="profile-price" /></div>
                    </div>
                    <Button onClick={save} disabled={saving} className="bg-primary gap-2" data-testid="save-profile"><Save size={16} /> {saving ? "Salvo…" : "Salva profilo"}</Button>
                </TabsContent>

                <TabsContent value="books" className="space-y-8">
                    {[{ k: "books_edited", t: "Testi su cui ha lavorato" }, { k: "own_novels", t: "Romanzi scritti dall'editor" }].map(({ k, t }) => (
                        <div key={k}>
                            <div className="flex items-center justify-between mb-3">
                                <h3 className="font-serif text-2xl">{t}</h3>
                                <Button size="sm" variant="outline" onClick={() => addItem(k, { title: "", author: "", year: "", publisher: "", cover_url: "" })} className="gap-1" data-testid={`add-${k}`}><Plus size={15} /> Aggiungi</Button>
                            </div>
                            <div className="space-y-4">
                                {(form[k] || []).map((b, i) => (
                                    <div key={b._key || `${k}-${i}`} className="bg-card border border-border rounded-lg p-4 grid sm:grid-cols-[auto_1fr] gap-4">
                                        <ImageUpload label="Copertina" value={b.cover_url} onChange={(u) => updItem(k, i, { cover_url: u })} testid={`upload-${k}-${i}`} />
                                        <div className="grid grid-cols-2 gap-3">
                                            <Input placeholder="Titolo" value={b.title} onChange={(e) => updItem(k, i, { title: e.target.value })} data-testid={`${k}-title-${i}`} />
                                            <Input placeholder="Autore" value={b.author} onChange={(e) => updItem(k, i, { author: e.target.value })} />
                                            <Input placeholder="Anno" value={b.year} onChange={(e) => updItem(k, i, { year: e.target.value })} />
                                            <Input placeholder="Editore" value={b.publisher} onChange={(e) => updItem(k, i, { publisher: e.target.value })} />
                                            <Button variant="ghost" size="sm" onClick={() => delItem(k, i)} className="col-span-2 text-destructive gap-1 justify-start"><Trash2 size={14} /> Rimuovi</Button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))}
                    <Button onClick={save} disabled={saving} className="bg-primary gap-2" data-testid="save-books"><Save size={16} /> Salva</Button>
                </TabsContent>

                <TabsContent value="services" className="space-y-4">
                    <div className="flex items-center justify-between">
                        <h3 className="font-serif text-2xl">Servizi e tariffe</h3>
                        <Button size="sm" variant="outline" onClick={() => addItem("services", { name: "", price: 0, unit: "a cartella", description: "" })} className="gap-1" data-testid="add-service"><Plus size={15} /> Aggiungi</Button>
                    </div>
                    {(form.services || []).map((s, i) => (
                        <div key={s._key || `svc-${i}`} className="bg-card border border-border rounded-lg p-4 space-y-3">
                            <div className="grid sm:grid-cols-3 gap-3">
                                <Input placeholder="Nome servizio" value={s.name} onChange={(e) => updItem("services", i, { name: e.target.value })} data-testid={`service-name-${i}`} />
                                <Input type="number" placeholder="Prezzo" value={s.price} onChange={(e) => updItem("services", i, { price: Number(e.target.value) })} data-testid={`service-price-${i}`} />
                                <Input placeholder="Unità (es. a cartella)" value={s.unit} onChange={(e) => updItem("services", i, { unit: e.target.value })} />
                            </div>
                            <Textarea placeholder="Descrizione" value={s.description} onChange={(e) => updItem("services", i, { description: e.target.value })} rows={2} />
                            <Button variant="ghost" size="sm" onClick={() => delItem("services", i)} className="text-destructive gap-1"><Trash2 size={14} /> Rimuovi</Button>
                        </div>
                    ))}
                    <Button onClick={save} disabled={saving} className="bg-primary gap-2" data-testid="save-services"><Save size={16} /> Salva</Button>
                </TabsContent>

                <TabsContent value="quotes" className="space-y-4">
                    {quotes.length === 0 && <p className="text-muted-foreground py-8 text-center">Nessuna richiesta ricevuta.</p>}
                    {quotes.map((q) => (
                        <div key={q.request_id} className="bg-card border border-border rounded-lg p-5" data-testid={`quote-${q.request_id}`}>
                            <div className="flex items-start justify-between gap-3 flex-wrap">
                                <div>
                                    <p className="font-semibold">{q.writer_name} <span className="text-muted-foreground font-normal">· {q.writer_email}</span></p>
                                    <p className="text-sm text-muted-foreground mt-0.5">{[q.genre, q.word_count, q.deadline].filter(Boolean).join(" · ")}</p>
                                </div>
                                <Select value={q.status} onValueChange={(v) => setStatus(q.request_id, v)}>
                                    <SelectTrigger className="w-[150px]" data-testid={`quote-status-${q.request_id}`}><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="In attesa">In attesa</SelectItem>
                                        <SelectItem value="Risposto">Risposto</SelectItem>
                                        <SelectItem value="In corso">In corso</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <p className="mt-3 text-muted-foreground whitespace-pre-wrap">{q.message}</p>
                        </div>
                    ))}
                </TabsContent>
            </Tabs>
        </div>
    );
}
